import React, { useState, useRef, useContext, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { c as classnames } from './index-2586sEvh.js';
import { P as Prices } from './Prices-J1SqTpik.js';
import Modal from './components/Modal/index.js';
import { isProductAvailableForSale, formatProductName, extractVariantsFromProductName, getAddonName } from './utils/product.js';
import { getPrices } from './utils/price.js';
import { logImgLoadErrorToRelic } from './utils/logToRelic.js';
import { a as ProductType } from './product-_pRduMz7.js';
import { CoreContext } from './context/index.js';

const RelatedProducts = ({ items, parentProductType, checkedAddon, quantityCounter, selectedProductVariant, selectedRelatedProductsSku, onRelatedProductClick, }) => {
    // const { t: tValidation } = useTranslation('', { keyPrefix: '$common.$validation' });
    const { t: tLabel } = useTranslation('', { keyPrefix: '$common.$labels' });
    const { asPath, locale } = useRouter();
    const [showProductModal, setShowProductModal] = useState(null);
    const openModalRef = useRef(null);
    const { data: { siteConfig: { product: { addonModalSize = 'lg' } = {}, productNameFilters, noZeroAfterDecimal = false, }, }, } = useContext(CoreContext);
    const isInStockVariant = useMemo(() => !selectedProductVariant ||
        isProductAvailableForSale(selectedProductVariant.backorder_status, selectedProductVariant.stock_status), [selectedProductVariant]);
    const clickProductImage = (item) => {
        if (isInStockVariant) {
            setShowProductModal(item);
        }
    };
    const onKeyPressProductImage = (e, item) => {
        if (e.key === 'Enter' && isInStockVariant) {
            setShowProductModal(item);
        }
    };
    const closeHandler = () => {
        setShowProductModal(null);
        if (openModalRef.current) {
            openModalRef.current.focus();
        }
    };
    const checkInStock = (item) => {
        const isRelatedProductIsAvailableForSale = isProductAvailableForSale(item.backorder_status, item.stock_status);
        if (!selectedProductVariant && isRelatedProductIsAvailableForSale)
            return true;
        return (selectedProductVariant &&
            isProductAvailableForSale(selectedProductVariant.backorder_status, selectedProductVariant.stock_status) &&
            isRelatedProductIsAvailableForSale);
    };
    const onKeyDown = (e) => {
        // Do not allow to submit a form on "Enter" key down
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };
    const onChange = (sku, name) => {
        const addonName = getAddonName(name);
        // if product is configurable send name
        if (parentProductType !== ProductType.Simple) {
            onRelatedProductClick(addonName);
            return;
        }
        // send sku for default behaviour on simple products
        onRelatedProductClick(sku);
    };
    const isDisabled = (item) => !isProductAvailableForSale(item.backorder_status, item.stock_status) ||
        !checkInStock(item) ||
        (selectedProductVariant &&
            !isProductAvailableForSale(selectedProductVariant.backorder_status, selectedProductVariant.stock_status));
    const isChecked = (item) => {
        // we use checkedAddon if it's a configurable product
        if (parentProductType !== ProductType.Simple) {
            return !isDisabled(item) && (checkedAddon === null || checkedAddon === void 0 ? void 0 : checkedAddon.includes(getAddonName(item.name_en.product_name)));
        }
        // we use selectedRelatedProductsSku for simple produts
        return selectedRelatedProductsSku.includes(item.sku);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "related-product" }, items.map(item => {
            const productName = formatProductName({
                name: item.name,
                lang: locale,
                siteFilters: productNameFilters || null,
            });
            const variants = extractVariantsFromProductName({
                name: item.name,
                siteFilters: productNameFilters || null,
            }).split(';');
            const prices = getPrices(item.price_range, locale, noZeroAfterDecimal);
            return (React.createElement("div", { key: item.id, className: "related-product-container" },
                React.createElement("div", { className: "related-product-checkbox" },
                    React.createElement("input", { "data-testid": "related-product-checkbox", type: "checkbox", checked: isChecked(item), onKeyDown: onKeyDown, onChange: () => onChange(item.sku, item.name_en.product_name), disabled: isDisabled(item), "aria-labelledby": "relatedProductInfo" })),
                React.createElement("div", { className: classnames('related-product-card', {
                        'out-of-stock': !isInStockVariant || !checkInStock(item),
                    }) },
                    React.createElement("div", { role: "button", tabIndex: 0, ref: openModalRef, "aria-label": tLabel('showFullImageSize'), onKeyPress: e => onKeyPressProductImage(e, item), className: "related-product-img", onClick: () => clickProductImage(item), style: { backgroundImage: `url(${item.thumbnail.url})` } }),
                    React.createElement("div", { role: "button", tabIndex: 0, onKeyDown: onKeyDown, onClick: () => onChange(item.sku, item.name_en.product_name), className: "related-product-info", id: "relatedProductInfo" },
                        React.createElement("span", null,
                            React.createElement("span", { className: "product-name" }, productName),
                            variants.map(variant => {
                                if (variant === '') {
                                    return null;
                                }
                                return (React.createElement("span", { className: "product-option", key: variant }, variant));
                            })),
                        checkInStock(item) ? (React.createElement(Prices, { show: true, prices: prices })) : (React.createElement("span", { className: "related-product-out-of-stock" }, tLabel('outStock'))),
                        quantityCounter > 1 && (React.createElement("span", null,
                            tLabel('qty'),
                            ": ",
                            quantityCounter))))));
        })),
        Boolean(showProductModal) && (React.createElement(Modal, { className: "related-product-modal", maxWidth: addonModalSize, open: true, title: showProductModal.name, onClose: closeHandler },
            React.createElement("div", { className: "related-product-modal-wrapper" }, (showProductModal === null || showProductModal === void 0 ? void 0 : showProductModal.small_image) && (React.createElement("img", { src: showProductModal.small_image.url, alt: showProductModal.small_image.label, onError: () => logImgLoadErrorToRelic(asPath, showProductModal.small_image.url) })))))));
};

export { RelatedProducts as R };
//# sourceMappingURL=RelatedProducts-wOAHevPP.js.map
